import { SeverityNumber } from '@opentelemetry/api-logs'

/**
 * PostHog logging service.
 *
 * The single module responsible for talking to PostHog about logs. Every
 * `logger.info` / `logger.warn` / `logger.error` call in the app is funnelled
 * through `postHogLoggingService.record()`; nothing else shapes log or
 * exception payloads for PostHog.
 *
 * Two channels leave here:
 *
 * 1. **OTLP log records** — one per log line, tagged with a `service`
 *    attribute so the PostHog infra panel can slice by subsystem. Gated on
 *    {@link isOtlpLogExportEnabled} (on by default in production).
 * 2. **`$exception` events** — every ERROR is also captured as an exception so
 *    `$exception_type` and `$exception_message` are populated. Previously the
 *    only exceptions PostHog saw were unhandled client-side crashes, which
 *    left both fields null and made message filtering impossible.
 *
 * This module deliberately imports no PostHog SDK. `posthog-node` must not be
 * pulled into client bundles and `posthog-js` must not be pulled into server
 * bundles, so the two transports register themselves at runtime from the
 * entry point that already owns the relevant SDK:
 *
 * - `instrumentation.ts` (Node runtime) → `posthog-node`
 * - `instrumentation-client.ts` (browser) → `posthog-js`
 *
 * When no transport is registered — scripts, tests, the Edge runtime — every
 * call here is a silent no-op.
 */

export type LogLevel = 'INFO' | 'WARN' | 'ERROR'

/**
 * PostHog's OTLP log receiver, used by the exporter in `instrumentation.ts`.
 *
 * The path is `/i/v1/logs`, matching `POSTHOG_TRACES_URL`'s `/i/v1/traces`.
 * `/otlp/v1/logs` — the obvious guess, and what this used to point at — is a
 * 404, and the exporter does not reject `forceFlush()` on an HTTP error, so
 * every record was dropped without a warning.
 */
export const POSTHOG_LOGS_URL = 'https://us.i.posthog.com/i/v1/logs'

/**
 * Whether server logs are exported to PostHog over OTLP — the feed the infra
 * panel is built on.
 *
 * `POSTHOG_OTLP_LOGS` wins when it is set either way. Unset, it defaults on in
 * production only: shipping logs to PostHog US is a cross-border data flow, so
 * preview and development deployments stay quiet unless somebody opts them in.
 */
export function isOtlpLogExportEnabled(
  processEnv: { POSTHOG_OTLP_LOGS?: string; VERCEL_ENV?: string; NODE_ENV?: string } = process.env
): boolean {
  const explicit = processEnv.POSTHOG_OTLP_LOGS
  if (explicit === 'true') return true
  if (explicit === 'false') return false
  return processEnv.VERCEL_ENV === 'production' || (!processEnv.VERCEL_ENV && processEnv.NODE_ENV === 'production')
}

/**
 * Canonical `service` tags. A log line's service identifies the subsystem or
 * feature it came from and is what the PostHog infra panel groups by, so the
 * spelling has to be stable — `prisma`, not `Prisma` or `db-prisma`.
 *
 * `LogService` still accepts any string (route-scoped tags like
 * `admin/products` are fine); this list is the shared vocabulary for the
 * infra and critical-flow panels.
 */
export const LOG_SERVICES = [
  // Infrastructure
  'prisma',
  'dynamo',
  'redis',
  'cache:remote',
  's3',
  'email',
  'proxy',
  'cron',
  'cron/barcode-backfill',
  'cron/ingredient-import',
  'cron/raw-ingredient-retention',
  'posthog-ingest',
  // Third-party APIs
  'inciapi',
  'gcv',
  // Auth
  'auth',
  'pre-auth',
  'user/lookup',
  'user/verify',
  // Product flows
  'scan',
  'scan-barcode',
  'scan-camera',
  'scan-capture',
  'label-scan',
  'face-analysis',
  'diary-face',
  'diary',
  'routine',
  'ingredient-analysis',
  'ingredient-resolver',
  'barcode-backfill',
  'ingredients/bulk-import',
  'ingredients/retention',
  'privacy',
  'survey',
  // Error boundaries
  'error-boundary',
  'global-error-boundary',
] as const

export type KnownLogService = (typeof LOG_SERVICES)[number]
/** Any string is accepted; {@link LOG_SERVICES} documents the canonical set. */
export type LogService = KnownLogService | (string & {})

/**
 * Context keys that must never reach PostHog: call sites pass arbitrary
 * context objects and these keys carry (or tend to carry) personal data. The
 * user id (pseudonymous cuid) intentionally stays for debugging.
 *
 * `instrumentation.ts` reuses this list for its OTLP processor so records
 * emitted outside this service are scrubbed too.
 */
export const PII_ATTRIBUTE_KEYS = [
  'email',
  'name',
  'ip',
  'query',
  'searchQuery',
  'textPreview',
  'rawTextPreview',
] as const

const REDACTED = '[redacted]'

/** Context keys read as the PostHog distinct id, in priority order. */
const DISTINCT_ID_KEYS = ['distinctId', 'userId'] as const

/**
 * Distinct id for server-side logs with no user attached. Paired with
 * `$process_person_profile: false` so these events never create a person.
 */
const ANONYMOUS_DISTINCT_ID = 'lamaven-server'

/**
 * Deployment environment tag, so the dashboard can separate production from
 * preview traffic. `VERCEL_ENV` is server-only; the browser falls back to
 * `NODE_ENV`, which Next inlines at build time.
 */
function currentEnvironment(): string {
  return process.env.VERCEL_ENV || process.env.NODE_ENV || 'development'
}

/**
 * Synthetic error type used when an ERROR is logged without a real `Error` —
 * e.g. `logger.error('redis', 'lock not acquired')`. Gives PostHog a non-null
 * `$exception_type`, with the log message as `$exception_message`.
 */
export class LoggedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LoggedError'
  }
}

/**
 * Sends an `$exception` event to PostHog. Implemented by whichever SDK is
 * available in the current runtime.
 */
export interface PostHogExceptionTransport {
  captureException(error: Error, properties: Record<string, unknown>, distinctId: string): void
}

/**
 * Registers the runtime's exception transport. Called once per runtime from
 * `instrumentation.ts` (server) and `instrumentation-client.ts` (browser).
 */
export function registerExceptionTransport(transport: PostHogExceptionTransport): void {
  globalThis.__posthogExceptionTransport = transport
}

function severityNumberFor(level: LogLevel): SeverityNumber {
  switch (level) {
    case 'INFO':
      return SeverityNumber.INFO
    case 'WARN':
      return SeverityNumber.WARN
    case 'ERROR':
      return SeverityNumber.ERROR
  }
}

/** Returns a copy of `context` with every {@link PII_ATTRIBUTE_KEYS} value redacted. */
export function scrubPii(context: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = { ...context }
  for (const key of PII_ATTRIBUTE_KEYS) {
    if (key in scrubbed) scrubbed[key] = REDACTED
  }
  return scrubbed
}

/**
 * Resolves the error to attach to the `$exception` event. A real `Error` is
 * used as-is so PostHog gets its type and stack; anything else (a string, a
 * rejected non-Error, nothing at all) becomes a {@link LoggedError} carrying
 * the log message.
 */
function toException(message: string, error: unknown): Error {
  if (error instanceof Error) return error
  if (error !== undefined && error !== null) return new LoggedError(`${message}: ${String(error)}`)
  return new LoggedError(message)
}

function distinctIdFrom(context: Record<string, unknown>): string | undefined {
  for (const key of DISTINCT_ID_KEYS) {
    const value = context[key]
    if (typeof value === 'string' && value.length > 0) return value
  }
  return undefined
}

/**
 * Emits one OTLP log record. No-op until OTLP export is enabled.
 *
 * `service.name` is the attribute the infra panel's "errors by service"
 * breakdown groups on — without it every server error lands in `other`. Note
 * it carries the *subsystem* (`prisma`, `redis`, …), not the application name;
 * the app is identified by the resource-level `service.name` in
 * `instrumentation.ts`. `service` is kept alongside it as the flat alias the
 * rest of the dashboard filters on.
 */
function emitLogRecord(
  level: LogLevel,
  service: LogService,
  message: string,
  context: Record<string, unknown>,
  distinctId: string | undefined,
  exception: Error | null
): void {
  const otelLogger = globalThis.__posthogLogger
  if (!otelLogger) return

  otelLogger.emit({
    severityNumber: severityNumberFor(level),
    severityText: level,
    body: message,
    attributes: {
      ...(context as Record<string, string | number | boolean>),
      service,
      'service.name': service,
      ...(distinctId ? { 'user.id': distinctId } : {}),
      ...(exception
        ? {
            'exception.type': exception.name,
            'exception.message': exception.message,
            ...(exception.stack ? { 'exception.stacktrace': exception.stack } : {}),
          }
        : {}),
    },
  })
}

/** Sends one `$exception` event with `$exception_type` / `$exception_message` set. */
function captureException(
  level: LogLevel,
  service: LogService,
  message: string,
  context: Record<string, unknown>,
  exception: Error,
  distinctId: string | undefined
): void {
  const transport = globalThis.__posthogExceptionTransport
  if (!transport) return

  transport.captureException(
    exception,
    {
      ...context,
      service,
      environment: currentEnvironment(),
      log_level: level,
      log_message: message,
      // Set explicitly rather than left to the SDK: these are the fields the
      // infra panel filters on and they must never come through as null.
      $exception_type: exception.name,
      $exception_message: exception.message,
      ...(distinctId ? {} : { $process_person_profile: false }),
    },
    distinctId ?? ANONYMOUS_DISTINCT_ID
  )
}

export interface LogRecord {
  level: LogLevel
  service: LogService
  message: string
  context?: Record<string, unknown>
  /** The caught value, when there is one. Populates `$exception_type` / `$exception_message`. */
  error?: unknown
}

export const postHogLoggingService = {
  /**
   * Processes one log line: PII-scrubs the context, emits an OTLP record, and
   * for ERROR also captures a PostHog `$exception`.
   *
   * Never throws — a broken analytics pipeline must not take down a request.
   */
  record({ level, service, message, context, error }: LogRecord): void {
    try {
      const scrubbed = scrubPii(context ?? {})
      const distinctId = distinctIdFrom(scrubbed)
      // Resolved once so the OTLP record's exception.* attributes and the
      // $exception event describe the same error.
      const exception = level === 'ERROR' ? toException(message, error) : null

      emitLogRecord(level, service, message, scrubbed, distinctId, exception)
      if (exception) captureException(level, service, message, scrubbed, exception, distinctId)
    } catch {
      // Swallowed on purpose: logging must never be the reason a request fails.
    }
  },
}
